import { useMemo, useState, useCallback, useRef, useEffect, memo } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import { CalendarDays } from "lucide-react";
import { format, getISOWeek } from "date-fns";
import { Button } from "@/v2/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/v2/components/ui/popover";
import { MonthPicker } from "@/shared/components/ui/monthpicker";
import { cn } from "@/v2/lib/utils";
import { emptyDaySums as emptyDay, type DaySums, type CalendarViewMode } from "@/v2/pages/Calendar";
import { getWeekdayLabels, getWeekStart, getWeekDays } from "@/v2/lib/weekUtils";

// InfiniteCalendar keeps its visual aggregating exported/tracked into single
// totals (billable + non-billable). Day cells are too small for the 4-way
// breakdown; that's a follow-up if it's wanted on mobile.
const expTotal = (s: DaySums) => s.exportedBillable + s.exportedNonBillable;
const trTotal = (s: DaySums) => s.trackedBillable + s.trackedNonBillable;

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────

const INITIAL_WEEKS = 26;
const LOAD_THRESHOLD = 300; // px from edge
const LOAD_BATCH = 8; // ~2 months
const WEEK_ROW_HEIGHT = 56; // h-14

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

function isSameDay(a: Date, b: Date) {
	return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function dayKey(d: Date) {
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDateParam(d: Date) {
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Generate an array of week-start days (firm-configured) centered around today */
function generateInitialWeeks(center: Date, count: number): Date[] {
	const centerWeek = getWeekStart(center);
	const half = Math.floor(count / 2);
	const weeks: Date[] = [];
	for (let i = -half; i < count - half; i++) {
		const w = new Date(centerWeek);
		w.setDate(w.getDate() + i * 7);
		weeks.push(w);
	}
	return weeks;
}

// ─────────────────────────────────────────────
// WeekRow (memoized)
// ─────────────────────────────────────────────

interface WeekRowProps {
	weekStart: Date;
	dailySums: Map<string, DaySums>;
	viewMode: CalendarViewMode;
	today: Date;
	selectedDate: Date;
	onSelectDate: (d: Date) => void;
	onSelectWeek: (from: Date, to: Date) => void;
	prevWeekStart: Date | null;
	dateRange?: { from?: Date; to?: Date };
	monthlySums: Map<string, DaySums>;
	yearlyTotal: DaySums;
}

const WeekRow = memo(function WeekRow({
	weekStart,
	dailySums,
	viewMode,
	today,
	selectedDate,
	onSelectDate,
	onSelectWeek,
	prevWeekStart,
	dateRange,
	monthlySums,
	yearlyTotal,
}: WeekRowProps) {
	const days = useMemo(() => getWeekDays(weekStart), [weekStart]);
	const weekNum = getISOWeek(days[3]); // Thursday determines ISO week

	// Week totals (collapse the 4-way breakdown into two totals for the compact row)
	const weekTotal = useMemo(() => {
		let exported = 0, tracked = 0;
		for (const d of days) {
			const ds = dailySums.get(dayKey(d));
			if (ds) { exported += expTotal(ds); tracked += trTotal(ds); }
		}
		return { exported: Math.round(exported * 10) / 10, tracked: Math.round(tracked * 10) / 10 };
	}, [days, dailySums]);

	// Is this the first week of a new month? Check if any day is the 1st and prev week didn't contain it
	const monthTransition = useMemo(() => {
		for (const d of days) {
			if (d.getDate() === 1) return d;
		}
		return null;
	}, [days]);

	const hasRange = dateRange?.from != null && dateRange?.to != null;

	return (
		<>
			{/* Month separator — sticks to top of scroll container */}
			{monthTransition && (() => {
				const monthKey = `${monthTransition.getFullYear()}-${String(monthTransition.getMonth() + 1).padStart(2, "0")}`;
				const ms = monthlySums.get(monthKey) || emptyDay();
				const msExp = expTotal(ms);
				const msTr = trTotal(ms);
				const ytdExp = expTotal(yearlyTotal);
				const ytdTr = trTotal(yearlyTotal);
				const isCurrentYearMonth = monthTransition.getFullYear() === today.getFullYear() && monthTransition.getMonth() === today.getMonth();
				return (
					// data-month is on a zero-height anchor (not the sticky div) so scrollIntoView
					// works correctly even when the sticky header is already pinned at the top
					<><div data-month={monthKey} className="h-0" /><div className="sticky top-0 z-[5] bg-secondary/80 backdrop-blur-sm px-3 py-1.5 border-b border-border flex items-center justify-between gap-2">
						<span className="text-xs font-semibold text-foreground">
							{format(monthTransition, "MMMM yyyy")}
						</span>
						<div className="flex items-center gap-3 text-[10px] tabular-nums">
							{(msExp > 0 || msTr > 0) && (
								<span>
									{viewMode !== "tracked" && <span className="font-semibold">{msExp.toFixed(1)}h</span>}
									{viewMode === "all" && <span className="text-muted-foreground"> · </span>}
									{viewMode !== "exported" && <span className="text-muted-foreground">{msTr.toFixed(1)}h</span>}
								</span>
							)}
							{isCurrentYearMonth && (ytdExp > 0 || ytdTr > 0) && (
								<span className="text-muted-foreground/70 border-l border-border pl-3">
									YtD{" "}
									{viewMode !== "tracked" && <span className="font-medium text-foreground/70">{ytdExp.toFixed(1)}h</span>}
									{viewMode === "all" && <span> · </span>}
									{viewMode !== "exported" && <span>{ytdTr.toFixed(1)}h</span>}
								</span>
							)}
						</div>
					</div></>
				);
			})()}
			<div
				className="grid h-14 border-b border-border"
				style={{ gridTemplateColumns: "repeat(7, 1fr) auto" }}
			>
				{days.map((d, i) => {
					const isDayToday = isSameDay(d, today);
					const isSelected = !hasRange && isSameDay(d, selectedDate);
					const isFuture = d.getTime() > today.getTime() && !isDayToday;
					const ds = dailySums.get(dayKey(d));
					const dsExp = ds ? expTotal(ds) : 0;
					const dsTr = ds ? trTotal(ds) : 0;
					const hasAnyData = dsExp > 0 || dsTr > 0;
					const showExported = hasAnyData && viewMode !== "tracked";
					const showTracked = hasAnyData && viewMode !== "exported";

					const prevDay = i === 0 ? (prevWeekStart ? new Date(prevWeekStart.getFullYear(), prevWeekStart.getMonth(), prevWeekStart.getDate() + 6) : null) : days[i - 1];
					const nextDay = i < 6 ? days[i + 1] : null;
					const showPrefix = (prevDay && prevDay.getMonth() !== d.getMonth()) || (nextDay && nextDay.getMonth() !== d.getMonth());

					return (
						<button
							key={i}
							onClick={() => onSelectDate(d)}
							className={cn(
								"flex flex-col items-start p-1 transition-colors",
								i > 0 && "border-l border-border",
								isFuture && "text-muted-foreground/50",
								isDayToday && "ring-1 ring-inset ring-highlight",
								isSelected && !isDayToday && "ring-1 ring-inset ring-primary",
							)}
						>
							{/* Date — top left */}
							<div className="flex items-center gap-0.5">
								{showPrefix && (
									<span className="text-[8px] text-muted-foreground leading-none">
										{format(d, "MMM")}
									</span>
								)}
								{isDayToday ? (
									<span className="flex h-5 w-5 items-center justify-center rounded-full bg-highlight text-white text-[10px] font-medium">
										{d.getDate()}
									</span>
								) : isSelected ? (
									<span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-medium">
										{d.getDate()}
									</span>
								) : (
									<span className="text-[10px] text-muted-foreground">{d.getDate()}</span>
								)}
							</div>
							{/* Hours — bottom left */}
							{(showExported || showTracked) && (
								<div className="mt-auto flex flex-col items-start gap-0 leading-none">
									{showExported && (
										<span className="text-[8px] font-medium tabular-nums">{dsExp.toFixed(1)}h</span>
									)}
									{showTracked && (
										<span className="text-[8px] text-muted-foreground tabular-nums">{dsTr.toFixed(1)}h</span>
									)}
								</div>
							)}
						</button>
					);
				})}

				{/* Week cell — desktop style */}
				<button
					onClick={() => {
						const from = days[0];
						const to = days[6];
						onSelectWeek(from, to);
					}}
					className="flex flex-col items-end justify-between border-l-2 border-border bg-muted/20 p-1 min-w-[44px] hover:bg-muted/40 transition-colors"
				>
					<span className="text-[8px] font-medium text-muted-foreground/60">W{weekNum}</span>
					{(weekTotal.exported > 0 || weekTotal.tracked > 0) && (
						<div className="flex flex-col items-end gap-0 leading-none mt-auto">
							{viewMode !== "tracked" && (
								<span className="text-[9px] font-semibold tabular-nums text-right min-w-[2.5em]">{weekTotal.exported.toFixed(1)}h</span>
							)}
							{viewMode !== "exported" && (
								<span className="text-[9px] text-muted-foreground font-medium tabular-nums text-right min-w-[2.5em]">{weekTotal.tracked.toFixed(1)}h</span>
							)}
						</div>
					)}
				</button>
			</div>
		</>
	);
});

// ─────────────────────────────────────────────
// InfiniteCalendar
// ─────────────────────────────────────────────

interface InfiniteCalendarProps {
	dailySums?: Map<string, DaySums>;
	viewMode: CalendarViewMode;
	onViewModeChange: (mode: CalendarViewMode) => void;
	selectedDate: Date;
	dateRange?: { from?: Date; to?: Date };
}

export function InfiniteCalendar({
	dailySums = new Map(),
	viewMode,
	onViewModeChange,
	selectedDate,
	dateRange,
}: InfiniteCalendarProps) {
	const navigate = useNavigate();
	const today = useMemo(() => new Date(), []);

	// Monthly sums: key = "YYYY-MM". Keeps the full 4-way breakdown so callers can
	// derive whichever totals they want via expTotal()/trTotal().
	const monthlySums = useMemo(() => {
		const sums = new Map<string, DaySums>();
		dailySums.forEach((ds, key) => {
			const monthKey = key.substring(0, 7);
			const prev = sums.get(monthKey) ?? emptyDay();
			prev.exportedBillable += ds.exportedBillable;
			prev.exportedNonBillable += ds.exportedNonBillable;
			prev.trackedBillable += ds.trackedBillable;
			prev.trackedNonBillable += ds.trackedNonBillable;
			sums.set(monthKey, prev);
		});
		return sums;
	}, [dailySums]);

	// Yearly total for current year
	const yearlyTotal = useMemo(() => {
		const year = today.getFullYear();
		const acc = emptyDay();
		monthlySums.forEach((v, k) => {
			if (k.startsWith(`${year}-`)) {
				acc.exportedBillable += v.exportedBillable;
				acc.exportedNonBillable += v.exportedNonBillable;
				acc.trackedBillable += v.trackedBillable;
				acc.trackedNonBillable += v.trackedNonBillable;
			}
		});
		return acc;
	}, [monthlySums, today]);

	const scrollRef = useRef<HTMLDivElement>(null);
	const [weeks, setWeeks] = useState(() => generateInitialWeeks(today, INITIAL_WEEKS));
	const isLoadingRef = useRef(false);

	// Track which month is most visible (for header display)
	const [visibleMonth, setVisibleMonth] = useState(() => {
		const anchor = dateRange?.from ?? selectedDate;
		return { year: anchor.getFullYear(), month: anchor.getMonth() };
	});

	// Initial scroll: snap to selected date or range start
	useEffect(() => {
		if (!scrollRef.current) return;
		const anchor = dateRange?.from ?? selectedDate;
		const monthKey = `${anchor.getFullYear()}-${String(anchor.getMonth() + 1).padStart(2, "0")}`;
		const sep = scrollRef.current.querySelector(`[data-month="${monthKey}"]`);
		if (sep) {
			sep.scrollIntoView({ block: "start", behavior: "auto" });
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Scroll handler: load more weeks + track visible month
	const handleScroll = useCallback(() => {
		const el = scrollRef.current;
		if (!el || isLoadingRef.current) return;

		// Load more at top
		if (el.scrollTop < LOAD_THRESHOLD) {
			isLoadingRef.current = true;
			const prevHeight = el.scrollHeight;
			flushSync(() => {
				setWeeks(prev => {
					const first = prev[0];
					const newWeeks: Date[] = [];
					for (let i = LOAD_BATCH; i > 0; i--) {
						const w = new Date(first);
						w.setDate(first.getDate() - i * 7);
						newWeeks.push(w);
					}
					return [...newWeeks, ...prev];
				});
			});
			// DOM is now committed — compensate scroll
			el.scrollTop += el.scrollHeight - prevHeight;
			isLoadingRef.current = false;
		}

		// Load more at bottom
		else if (el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_THRESHOLD) {
			isLoadingRef.current = true;
			setWeeks(prev => {
				const last = prev[prev.length - 1];
				const newWeeks: Date[] = [];
				for (let i = 1; i <= LOAD_BATCH; i++) {
					const w = new Date(last);
					w.setDate(last.getDate() + i * 7);
					newWeeks.push(w);
				}
				return [...prev, ...newWeeks];
			});
			requestAnimationFrame(() => {
				isLoadingRef.current = false;
			});
		}

		// Track visible month — find the last month separator above the viewport midpoint
		const separators = el.querySelectorAll("[data-month]");
		const midY = el.getBoundingClientRect().top + el.clientHeight / 2;
		let closestMonth: string | null = null;
		for (const sep of separators) {
			if (sep.getBoundingClientRect().top <= midY) {
				closestMonth = sep.getAttribute("data-month");
			}
		}
		if (closestMonth) {
			const [yearStr, monthStr] = closestMonth.split("-");
			const y = parseInt(yearStr, 10);
			const m = parseInt(monthStr, 10) - 1;
			setVisibleMonth(prev => {
				if (prev.year === y && prev.month === m) return prev;
				return { year: y, month: m };
			});
		}
	}, [weeks]);

	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;
		el.addEventListener("scroll", handleScroll, { passive: true });
		return () => el.removeEventListener("scroll", handleScroll);
	}, [handleScroll]);

	const onSelectDate = useCallback((d: Date) => {
		if (isSameDay(d, today)) {
			navigate("/tracked");
		} else {
			navigate(`/tracked?date=${formatDateParam(d)}`);
		}
	}, [today, navigate]);

	const onSelectWeek = useCallback((from: Date, to: Date) => {
		navigate(`/tracked?from=${formatDateParam(from)}&to=${formatDateParam(to)}`);
	}, [navigate]);

	// Scroll to a specific month
	const scrollToMonth = useCallback((year: number, month: number) => {
		const target = new Date(year, month, 1);
		const targetWeek = getWeekStart(target);

		// Make sure we have weeks covering this date
		const idx = weeks.findIndex(w => {
			const diff = targetWeek.getTime() - w.getTime();
			return diff >= 0 && diff < 7 * 24 * 60 * 60 * 1000;
		});

		if (idx === -1) {
			// Generate new weeks centered on target — flushSync ensures DOM is committed before scroll
			flushSync(() => {
				setWeeks(generateInitialWeeks(target, INITIAL_WEEKS));
			});
			requestAnimationFrame(() => {
				if (!scrollRef.current) return;
				const sep = scrollRef.current.querySelector(`[data-month="${year}-${String(month + 1).padStart(2, "0")}"]`);
				if (sep) {
					sep.scrollIntoView({ block: "start", behavior: "auto" });
				}
			});
		} else {
			if (!scrollRef.current) return;
			const sep = scrollRef.current.querySelector(`[data-month="${year}-${String(month + 1).padStart(2, "0")}"]`);
			if (sep) {
				sep.scrollIntoView({ block: "start", behavior: "smooth" });
			} else {
				// Fallback: approximate scroll
				scrollRef.current.scrollTo({ top: idx * WEEK_ROW_HEIGHT, behavior: "smooth" });
			}
		}
	}, [weeks]);

	const goToToday = useCallback(() => {
		scrollToMonth(today.getFullYear(), today.getMonth());
	}, [today, scrollToMonth]);

	const [pickerOpen, setPickerOpen] = useState(false);

	const visibleMonthDate = new Date(visibleMonth.year, visibleMonth.month, 1);

	return (
		<div className="flex flex-col h-full">
			{/* Header */}
			<div className="flex items-center gap-1.5 px-2 py-1 border-b border-border">
				<Button variant="ghost" size="sm" onClick={goToToday} className="h-10 text-sm gap-1.5 px-3">
					<CalendarDays className="size-4" />
					Today
				</Button>

				{/* Month label */}
				<div className="flex items-center flex-1 justify-center">
					<Popover open={pickerOpen} onOpenChange={setPickerOpen}>
						<PopoverTrigger asChild>
							<button className="text-base font-semibold hover:text-foreground/80 transition-colors px-2 h-10 flex items-center">
								{format(visibleMonthDate, "MMM yyyy")}
							</button>
						</PopoverTrigger>
						<PopoverContent align="center" className="w-auto p-0">
							<MonthPicker
								selectedMonth={visibleMonthDate}
								onMonthSelect={(d) => {
									scrollToMonth(d.getFullYear(), d.getMonth());
									setPickerOpen(false);
								}}
							/>
						</PopoverContent>
					</Popover>
				</div>

				{/* View mode dropdown */}
				<Select value={viewMode} onValueChange={(v) => onViewModeChange(v as CalendarViewMode)}>
					<SelectTrigger variant="ghost" className="h-10 w-[108px] text-sm justify-end gap-1.5">
						<SelectValue />
					</SelectTrigger>
					<SelectContent align="end">
						<SelectItem value="exported">Exported</SelectItem>
						<SelectItem value="tracked">Tracked</SelectItem>
						<SelectItem value="all">All</SelectItem>
					</SelectContent>
				</Select>
			</div>

			{/* Sticky weekday header */}
			<div
				className="grid sticky top-0 z-10 bg-secondary border-b border-border"
				style={{ gridTemplateColumns: "repeat(7, 1fr) auto" }}
			>
				{getWeekdayLabels("letter").map((day, i) => (
					<div
						key={i}
						className={cn(
							"py-2 text-center text-[11px] font-medium text-muted-foreground",
							i > 0 && "border-l border-border",
						)}
					>
						{day}
					</div>
				))}
				<div className="py-2 text-center text-[11px] font-medium text-muted-foreground border-l-2 border-border px-2 min-w-[40px]">
					Wk
				</div>
			</div>

			{/* Infinite scroll area */}
			<div ref={scrollRef} className="flex-1 overflow-y-auto">
				{weeks.map((weekStart, i) => (
					<WeekRow
						key={weekStart.getTime()}
						weekStart={weekStart}
						dailySums={dailySums}
						viewMode={viewMode}
						today={today}
						selectedDate={selectedDate}
						onSelectDate={onSelectDate}
						onSelectWeek={onSelectWeek}
						prevWeekStart={i > 0 ? weeks[i - 1] : null}
						dateRange={dateRange}
						monthlySums={monthlySums}
						yearlyTotal={yearlyTotal}
					/>
				))}
			</div>
		</div>
	);
}
